import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { AccessRule, AccessSummary } from '@/types/crm';

interface AccessPanelProps {
  access_summary: AccessSummary;
  org_name?: string;
  actions?: ReactNode;
}

// Allow only same-origin relative paths (starts with `/` but not `//`).
// Blocks `javascript:`, `data:`, `vbscript:`, and protocol-relative URLs.
function isSafeSettingsUrl(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

// Maps internal entity type names to user-facing labels. Never expose ahoy_* names.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  ahoy_meeting: 'meeting',
  ahoy_email_thread: 'email',
  ahoy_meeting_notes: 'notes',
  ahoy_file: 'file',
};

function parentLabel(entityType: string | null): string {
  if (!entityType) {
    return 'parent';
  }
  return ENTITY_TYPE_LABELS[entityType] ?? 'parent';
}

function getRuleCopy(
  rule: AccessRule,
  viewerIsOwner: boolean,
  orgName: string
): string {
  switch (rule.kind) {
    case 'EVERYONE_AT_ORG':
      return `Visible to everyone at ${orgName}`;
    case 'MEETING_ATTENDEES':
      return viewerIsOwner
        ? 'Visible to attendees on the invite'
        : "You're an attendee on this meeting";
    case 'THREAD_RECIPIENTS':
      return viewerIsOwner
        ? 'Visible to recipients on this thread'
        : "You're a recipient on this thread";
    case 'FILE_UPLOADER_ONLY':
      return viewerIsOwner
        ? 'Only you can see this file'
        : 'Visible to the file uploader only';
    case 'FILE_INHERITED_FROM_PARENT': {
      const label = parentLabel(rule.parent_entity_type);
      return `Inherited from the ${label} – anyone who can see the ${label} can see this file`;
    }
    case 'INHERITED_PARENT_HIDDEN':
      return 'Shared via context you can see';
    default:
      return '';
  }
}

export const AccessPanel = ({
  access_summary,
  org_name,
  actions,
}: AccessPanelProps) => {
  const { privacy_state, access_rules, settings_link, viewer_is_owner } =
    access_summary;
  const resolvedOrgName = org_name ?? 'your organization';
  const heading = privacy_state === 'org' ? 'Visible to org' : 'Private';

  return (
    <div className='flex flex-col gap-2 p-3'>
      <p className='text-body-small font-medium text-[var(--color-text-primary)]'>
        {heading}
      </p>
      {access_rules.length > 0 && (
        <ul className='flex flex-col gap-1'>
          {access_rules.map(rule => {
            const copy = getRuleCopy(rule, viewer_is_owner, resolvedOrgName);
            if (!copy) {
              return null;
            }
            const key = `${rule.kind}:${rule.parent_entity_type ?? ''}:${rule.parent_entity_id ?? ''}`;
            return (
              <li
                key={key}
                className='text-body-small text-[var(--color-text-secondary)]'
              >
                {copy}
              </li>
            );
          })}
        </ul>
      )}
      {settings_link && isSafeSettingsUrl(settings_link.url) && (
        <Link
          to={settings_link.url}
          className='text-body-small text-fg-brand-primary hover:underline'
        >
          Manage in {settings_link.provider_label} settings
        </Link>
      )}
      {actions}
    </div>
  );
};
